import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuxiliaryDataFieldSchemaInterface } from '@vendasta/account-group/lib/_internal/interfaces/fields.interface';
import { ActivityInterface, AutomationInterface, Comparison, WorkflowStepInterface } from '@vendasta/automata';
import { ObjectType, SchemaService } from '@vendasta/auxiliary-data-components';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { ActivityTableEntry, AutomationDisplayService, NodeData } from '../../common/automation-display.service';
import { CustomDataUpdateType } from './custom-data-updated-panel/custom-data-updated-panel.component';

const ObjectTypeTextMap = new Map([
  ['business', 'AUTOMATIONS.EDITOR.TRIGGERS.CUSTOM_DATA_UPDATED.OBJECT_TYPE.ACCOUNT'],
]);

@Injectable({ providedIn: 'root' })
export class CustomDataUpdatedService extends AutomationDisplayService {
  objectType: ObjectType = 'business';

  constructor(
    protected readonly translateService: TranslateService,
    protected readonly schemaService: SchemaService,
  ) {
    super();
  }

  getActivityColumn(
    namespace: string,
    activity: ActivityInterface,
    automation: AutomationInterface,
  ): ActivityTableEntry {
    const triggerData = activity.triggerData ? JSON.parse(activity.triggerData) : {};
    let fieldIds;
    try {
      fieldIds = JSON.parse(triggerData.updated_custom_field_ids);
    } catch {
      fieldIds = [];
    }
    const fieldSchemas$ = this.getMultiFieldSchema(fieldIds, namespace);

    const message$ = this.getFieldNames(fieldSchemas$).pipe(
      switchMap((fieldNames) =>
        this.translateService.stream('AUTOMATIONS.EDITOR.TRIGGERS.CUSTOM_DATA_UPDATED.ACTIVITY.TITLE', {
          objectType: this.getObjectTypeText(this.objectType),
          fieldsUpdated: fieldNames,
        }),
      ),
    );

    return this.tableEntry(message$, this.getDefaultStepColumnBody(namespace, activity.workflowStep!, automation));
  }

  getAutomationNodeData(
    namespace: string,
    step: WorkflowStepInterface,
    isWaitUntilNode: boolean,
  ): Observable<NodeData> {
    const comparisonType: CustomDataUpdateType = step?.choices?.rules[0]?.comparison as CustomDataUpdateType;
    let fieldIds: string[];
    try {
      fieldIds = JSON.parse(step.choices.rules[0].value);
    } catch {
      fieldIds = [];
    }

    const fieldSchemas$ = this.getMultiFieldSchema(fieldIds, namespace).pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    const fieldNamesDisplay$ = this.getFieldNames(fieldSchemas$);
    const anyFieldsArchived$ = this.areAnyFieldsArchived(fieldSchemas$);
    let message$ = fieldNamesDisplay$.pipe(
      switchMap((fieldNamesDisplay) =>
        this.translateService.stream('AUTOMATIONS.EDITOR.TRIGGERS.CUSTOM_DATA_UPDATED.ACTION.WHEN_TITLE', {
          objectType: this.getObjectTypeText(this.objectType).toLowerCase(),
          updateType: this.getComparisonText(comparisonType),
          fieldsUpdated: fieldNamesDisplay,
        }),
      ),
    );
    if (isWaitUntilNode) {
      message$ = fieldNamesDisplay$.pipe(
        switchMap((fieldNamesDisplay) =>
          this.translateService.stream('AUTOMATIONS.EDITOR.TRIGGERS.CUSTOM_DATA_UPDATED.ACTION.WAIT_UNTIL_TITLE', {
            objectType: this.getObjectTypeText(this.objectType).toLowerCase(),
            updateType: this.getComparisonText(comparisonType),
            fieldsUpdated: fieldNamesDisplay,
          }),
        ),
      );
    }

    if (!step.data) {
      return this.nodeDisplayData(
        message$,
        this.validationErrorsDetected(
          step,
          this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.CUSTOM_DATA_UPDATED.DEFAULT_CONFIG_ERROR'),
          true,
        ),
      );
    }

    return anyFieldsArchived$.pipe(
      switchMap((anyFieldsArchived) => {
        if (anyFieldsArchived) {
          return this.nodeDisplayData(
            message$,
            this.validationErrorsDetected(
              step,
              this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.CUSTOM_DATA_UPDATED.ARCHIVED_CONFIG_ERROR'),
              true,
            ),
          );
        }
        return this.nodeDisplayData(message$, this.noValidationErrorsDetected());
      }),
    );
  }

  getTemplateNodeData(_namespace: string, step: WorkflowStepInterface, isWaitUntilNode: boolean): Observable<NodeData> {
    const message = this.buildMessageFromTemplateData(isWaitUntilNode);
    return this.nodeDisplayData(
      message,
      this.validationErrorsDetected(
        step,
        this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.CUSTOM_DATA_UPDATED.DEFAULT_CONFIG_ERROR'),
        true,
      ),
    );
  }

  private getMultiFieldSchema(fieldIds: string[], partnerId: string): Observable<AuxiliaryDataFieldSchemaInterface[]> {
    return this.schemaService
      .getMultiFieldSchema(
        this.objectType,
        fieldIds.map((f) => ({ fieldId: f, partnerId: partnerId })),
      )
      .pipe(
        map((response) => response.fieldSchemas),
        catchError(() => of([])),
      );
  }

  private getFieldNames(fieldSchemas$: Observable<AuxiliaryDataFieldSchemaInterface[]>): Observable<string> {
    return fieldSchemas$.pipe(
      map((fieldSchemas) => {
        if (!fieldSchemas || fieldSchemas.length < 1) {
          return this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.CUSTOM_DATA_UPDATED.UNKNOWN_FIELDS');
        }
        return fieldSchemas.map((f) => `${f.fieldName}`).join(', ');
      }),
      map((listOfFields) => `<div>${listOfFields}</div>`),
    );
  }

  private areAnyFieldsArchived(fieldSchemas$: Observable<AuxiliaryDataFieldSchemaInterface[]>): Observable<boolean> {
    return fieldSchemas$.pipe(map((fieldSchemas) => !!fieldSchemas?.find((f) => !!f.archived)));
  }

  private getObjectTypeText(objectType: ObjectType): string {
    const objectTypeTextKey =
      ObjectTypeTextMap[objectType] || 'AUTOMATIONS.EDITOR.TRIGGERS.CUSTOM_DATA_UPDATED.OBJECT_TYPE.ACCOUNT';
    return this.translateService.instant(objectTypeTextKey);
  }

  private getComparisonText(comparisonType: CustomDataUpdateType): string {
    switch (comparisonType) {
      case Comparison.StringListContains:
        return this.translateService.instant(
          'AUTOMATIONS.EDITOR.TRIGGERS.CUSTOM_DATA_UPDATED.ACTION.ANY_FIELDS_UPDATED',
        );
      case Comparison.StringListContainsAll:
        return this.translateService.instant(
          'AUTOMATIONS.EDITOR.TRIGGERS.CUSTOM_DATA_UPDATED.ACTION.ALL_FIELDS_UPDATED',
        );
      default:
        console.warn('Invalid comparison type, check comparator for trigger');
        return '';
    }
  }

  private buildMessageFromTemplateData(isWaitUntilNode: boolean): Observable<string> {
    let message = this.translateService.stream(
      'AUTOMATIONS.EDITOR.TRIGGERS.CUSTOM_DATA_UPDATED.ACTION.TEMPLATE_TITLE',
      {
        objectType: this.getObjectTypeText(this.objectType).toLowerCase(),
      },
    );
    if (isWaitUntilNode) {
      message = this.translateService.stream(
        'AUTOMATIONS.EDITOR.TRIGGERS.CUSTOM_DATA_UPDATED.ACTION.WAIT_UNTIL_TEMPLATE_TITLE',
        {
          objectType: this.getObjectTypeText(this.objectType).toLowerCase(),
        },
      );
    }
    return message;
  }
}
